import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, Scale, Users, ArrowRight, Lock, Award, Briefcase } from "lucide-react";
import heroImg from "../assets/hero-courtroom.jpg";
import lauraImg from "../assets/laura-figari.png";
import gerardoImg from "../assets/gerardo-benitez.png";
import rodrigoImg from "../assets/rodrigo-monges.png";
import joseImg from "../assets/jose-mereles.jpg";
import javierImg from "../assets/javier-godoy.jpg";
import alejandroImg from "../assets/alejandro-fernandez.png";
import sergioImg from "../assets/sergio-canete.png";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Estudio Libertad E.A.E.S. — Consultores y Abogados en Asunción" },
      {
        name: "description",
        content:
          "Estudio Libertad E.A.E.S. · Consultores y Abogados. Siete socios. Práctica penal, procesal penal, civil, familia, niñez, comercial, societaria, sucesoria, laboral, inmobiliaria, notarial, contencioso administrativa, indemnizatoria patrimonial y penal patrimonial. Asunción, Paraguay.",
      },
      { property: "og:title", content: "Estudio Libertad E.A.E.S. — Consultores y Abogados" },
      {
        property: "og:description",
        content:
          "Práctica penal, civil, familia, comercial, societaria, sucesoria, laboral, inmobiliaria, notarial, contencioso administrativa e indemnizatoria patrimonial en Asunción, Paraguay.",
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <>
      {/* HERO */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden bg-navy-950">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Estudio Libertad E.A.E.S. — Consultores y Abogados"
            width={1920}
            height={1280}
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/70 to-navy-950/30" />
        </div>

        <div className="container-elegant relative py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="mb-6 inline-block text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-400">
              Consultores y Abogados · Asunción, Paraguay
            </span>
            <h1 className="font-serif text-4xl leading-tight text-stone-100 sm:text-5xl md:text-6xl lg:text-7xl">
              Asesoría jurídica integral para quienes
              <span className="italic text-gold-400"> exigen excelencia.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-100/75 md:text-lg">
              Estudio Libertad E.A.E.S. es una firma boutique conducida por siete socios. Práctica
              penal, procesal penal, civil, comercial, societaria, de familia, sucesoria, laboral,
              inmobiliaria, notarial, contencioso administrativa e indemnizatoria patrimonial con
              rigor técnico y absoluta confidencialidad.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center gap-3 bg-gold-500 px-8 py-4 text-[11px] font-semibold tracking-[0.2em] uppercase text-white transition-transform hover:scale-[1.02]"
              >
                Agendar Consulta
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/areas"
                className="inline-flex items-center justify-center border border-stone-100/30 px-8 py-4 text-[11px] font-semibold tracking-[0.2em] uppercase text-stone-100 transition-all hover:border-gold-400 hover:text-gold-400"
              >
                Áreas de Práctica
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="border-b border-navy-900/10 bg-stone-100 py-20">
        <div className="container-elegant grid gap-12 md:grid-cols-3">
          {[
            {
              icon: ShieldCheck,
              title: "Atención Directa",
              text: "Cada caso es atendido personalmente por uno de los socios del estudio.",
            },
            {
              icon: Lock,
              title: "Confidencialidad Absoluta",
              text: "Cada consulta protegida por el más estricto secreto profesional.",
            },
            {
              icon: Users,
              title: "Práctica Multidisciplinaria",
              text: "Penal, civil, comercial, societario, familia, niñez, sucesorio, laboral, inmobiliario, notarial, contencioso administrativo e indemnizatorio patrimonial.",
            },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex gap-5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold-500/40 text-gold-500">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="mb-2 font-serif text-xl text-navy-900">{title}</h3>
                <p className="text-sm leading-relaxed text-navy-900/65">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PARTNERS */}
      <section className="bg-stone-100 py-28 md:py-32">
        <div className="container-elegant">
          <div className="mx-auto mb-20 max-w-2xl text-center">
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Los Socios
            </span>
            <h2 className="mt-4 font-serif text-4xl text-navy-900 md:text-5xl">
              Siete prácticas complementarias, un mismo estudio
            </h2>
            <div className="mx-auto mt-6 h-px w-16 bg-gold-500" />
            <p className="mt-6 text-navy-900/65">
              Los siete socios del Estudio Libertad combinan experiencia en derecho penal, procesal
              penal, civil, comercial, societario, familia, niñez y adolescencia, sucesorio,
              laboral, inmobiliario, notarial, contencioso administrativo, indemnizatorio
              patrimonial e inversiones en valores.
            </p>
          </div>

          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
            <PartnerCard
              img={lauraImg}
              name="Abg. Laura Figari"
              role="Socia · Civil · Familia · Niñez y Adolescencia"
              bio="Especialista en derecho civil, derecho de familia y niñez y adolescencia. Acompaña con sensibilidad técnica los procesos más delicados del estudio."
            />
            <PartnerCard
              img={gerardoImg}
              name="Abg. Gerardo Benítez"
              role="Socio · Civil · Comercial · Sociedades · Penal Patrimonial"
              bio="Especialista en derecho civil, comercial y societario, con foco en penal patrimonial e inversiones en valores. Asesora a empresas, directivos e inversionistas."
            />
            <PartnerCard
              img={rodrigoImg}
              name="Abg. Rodrigo Monges Luces"
              role="Socio · Derecho Penal"
              bio="Especialista en derecho penal. Defensa estratégica en causas de alto perfil, delitos económicos y litigación compleja con atención directa y personal."
            />
            <PartnerCard
              img={joseImg}
              name="Abg. José Mereles"
              role="Socio · Sucesorio · Civil · Familia · Laboral · Inmobiliario · Notarial · Quiebras"
              bio="Especialista en derecho sucesorio, civil, familia, laboral, inmobiliario, asesoría notarial y convocatoria de acreedores y quiebras. Acompaña a personas, familias y empresas con un enfoque cercano y resolutivo."
            />
            <PartnerCard
              img={javierImg}
              name="Abg. Francisco Javier Godoy Moreno"
              role="Socio · Penal · Procesal Penal · Administrativo · Contencioso Administrativo"
              bio="Defensa técnica en causas penales y patrocinio frente a la Administración Pública en sumarios, sanciones y litigios contencioso-administrativos."
            />
            <PartnerCard
              img={alejandroImg}
              name="Abg. Alejandro Daniel Fernández"
              role="Socio · Penal · Procesal Penal · Civil y Comercial · Societario · Indemnizatorio"
              bio="Especialista en derecho penal, procesal penal, civil y comercial, societario e indemnizatorio patrimonial. Combina defensa técnica con asesoramiento corporativo."
            />
            <PartnerCard
              img={sergioImg}
              name="Abg. Sergio Cañete"
              role="Socio · Penal · Procesal Penal · Juicios Ejecutivos · Procesos Ordinarios · Laboral"
              bio="Especialista en derecho penal y procesal penal, juicios ejecutivos, procesos ordinarios y demandas laborales. Combina defensa técnica con litigación civil y laboral para personas y empresas."
            />
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/trayectoria"
              className="inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.2em] uppercase text-navy-900 hover:text-gold-500"
            >
              Conocer a los socios
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <section className="py-28 md:py-32">
        <div className="container-elegant">
          <div className="mx-auto mb-20 max-w-2xl text-center">
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Áreas de Práctica
            </span>
            <h2 className="mt-4 font-serif text-4xl text-navy-900 md:text-5xl">
              Especialización y rigor
            </h2>
            <div className="mx-auto mt-6 h-px w-16 bg-gold-500" />
            <p className="mt-6 text-navy-900/65">
              Una práctica integral conducida por seis socios, capaz de acompañar a personas,
              familias, empresas e inversionistas.
            </p>
          </div>

          <div className="grid gap-px bg-navy-900/10 md:grid-cols-3">
            {[
              {
                num: "01",
                title: "Derecho Penal",
                text: "Defensa estratégica en causas penales de alto perfil, delitos económicos, patrimoniales y litigación compleja.",
              },
              {
                num: "02",
                title: "Civil & Familia",
                text: "Derecho civil, familia, niñez y adolescencia. Procesos contenciosos y voluntarios atendidos con criterio técnico y humano.",
              },
              {
                num: "03",
                title: "Comercial & Valores",
                text: "Sociedades, contratos comerciales, asesoramiento corporativo e inversiones en el mercado de valores.",
              },
            ].map((item) => (
              <div
                key={item.num}
                className="group relative bg-stone-100 p-10 transition-colors duration-500 hover:bg-navy-900 md:p-12"
              >
                <span className="block font-serif text-3xl text-gold-500">{item.num}</span>
                <h3 className="mt-8 font-serif text-2xl text-navy-900 transition-colors group-hover:text-stone-100">
                  {item.title}
                </h3>
                <p className="mt-5 text-sm leading-relaxed text-navy-900/65 transition-colors group-hover:text-stone-100/70">
                  {item.text}
                </p>
                <div className="mt-10 h-px w-0 bg-gold-500 transition-all duration-500 group-hover:w-full" />
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/areas"
              className="inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.2em] uppercase text-navy-900 hover:text-gold-500"
            >
              Ver todas las áreas
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY / ABOUT */}
      <section className="bg-navy-900 py-28 text-stone-100 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="relative">
            <div className="grid grid-cols-3 gap-2 md:grid-cols-7">
              <img
                src={lauraImg}
                alt="Abg. Laura Figari"
                width={600}
                height={800}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover"
              />
              <img
                src={gerardoImg}
                alt="Abg. Gerardo Benítez"
                width={600}
                height={800}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover md:mt-6"
              />
              <img
                src={rodrigoImg}
                alt="Abg. Rodrigo Monges Luces"
                width={600}
                height={800}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover"
              />
              <img
                src={joseImg}
                alt="Abg. José Mereles"
                width={600}
                height={800}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover md:mt-6"
              />
              <img
                src={javierImg}
                alt="Abg. Francisco Javier Godoy Moreno"
                width={600}
                height={800}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover"
              />
              <img
                src={alejandroImg}
                alt="Abg. Alejandro Daniel Fernández"
                width={600}
                height={800}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover md:mt-6"
              />
              <img
                src={sergioImg}
                alt="Abg. Sergio Cañete"
                width={600}
                height={800}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden border border-gold-500/40 bg-navy-950 p-8 md:block">
              <Scale className="h-8 w-8 text-gold-500" />
              <p className="mt-4 max-w-[14rem] font-serif text-sm italic leading-relaxed text-stone-100/80">
                "La buena defensa empieza por la comprensión rigurosa de cada caso."
              </p>
            </div>
          </div>

          <div>
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-400">
              La Firma
            </span>
            <h2 className="mt-4 font-serif text-4xl leading-tight md:text-5xl">
              Un estudio construido sobre la confianza y el resultado.
            </h2>
            <div className="mt-6 h-px w-16 bg-gold-500" />
            <div className="mt-8 space-y-5 text-stone-100/70">
              <p>
                Con sede en Asunción, Estudio Libertad E.A.E.S. se proyecta como una firma boutique
                de servicios jurídicos integrales, conducida directamente por sus siete socios: la
                Abg. Laura Figari, el Abg. Gerardo Benítez, el Abg. Rodrigo Monges Luces, el Abg.
                José Mereles, el Abg. Francisco Javier Godoy Moreno, el Abg. Alejandro Daniel
                Fernández y el Abg. Sergio Cañete.
              </p>
              <p>
                La calidad de nuestro trabajo se mide en la solidez de las relaciones que
                construimos: clientes que regresan, empresas que confían y colegas que nos refieren.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-stone-100/10 pt-10">
              <Stat value="7" label="Socios" />
              <Stat value="100%" label="Confidencialidad" />
              <Stat value="24/7" label="Disponibilidad" />
            </div>

            <Link
              to="/trayectoria"
              className="mt-10 inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.2em] uppercase text-gold-400 hover:text-stone-100"
            >
              Conocer trayectoria
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* DIFFERENTIALS */}
      <section className="py-28 md:py-32">
        <div className="container-elegant">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Diferenciales
            </span>
            <h2 className="mt-4 font-serif text-4xl text-navy-900 md:text-5xl">
              Por qué eligen nuestro estudio
            </h2>
            <div className="mx-auto mt-6 h-px w-16 bg-gold-500" />
          </div>

          <div className="grid gap-10 md:grid-cols-2">
            {[
              {
                icon: Award,
                t: "Práctica Especializada",
                d: "Siete socios con áreas complementarias: penal, procesal penal, civil, familia, comercial, societario, sucesorio, laboral, inmobiliario, notarial, contencioso administrativo e indemnizatorio patrimonial.",
              },
              {
                icon: Briefcase,
                t: "Estudio Jurídico Propio",
                d: "Estructura formal en Asunción con equipo dedicado y procesos consolidados.",
              },
              {
                icon: Users,
                t: "Acompañamiento Integral",
                d: "Personas, familias, empresas e inversionistas atendidos por un solo equipo.",
              },
              {
                icon: ShieldCheck,
                t: "Profesionalismo Demostrable",
                d: "Trayectoria, casos resueltos y la confianza de empresas y particulares.",
              },
            ].map(({ icon: Icon, t, d }) => (
              <div
                key={t}
                className="flex gap-6 border-l-2 border-gold-500/30 pl-6 transition-colors hover:border-gold-500"
              >
                <Icon className="mt-1 h-6 w-6 shrink-0 text-gold-500" />
                <div>
                  <h3 className="font-serif text-2xl text-navy-900">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy-900/65">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-950 py-24 text-stone-100">
        <div className="container-elegant text-center">
          <h2 className="mx-auto max-w-3xl font-serif text-4xl leading-tight md:text-5xl">
            Cuando lo que está en juego importa,{" "}
            <span className="italic text-gold-400">la asesoría correcta es decisiva.</span>
          </h2>
          <p className="mx-auto mt-8 max-w-xl text-stone-100/65">
            Agende una consulta privada con el Estudio Libertad E.A.E.S. Atención bajo cita previa
            en nuestras oficinas de Asunción.
          </p>
          <Link
            to="/contacto"
            className="mt-10 inline-flex items-center gap-3 bg-gold-500 px-10 py-5 text-[11px] font-semibold tracking-[0.2em] uppercase text-white transition-transform hover:scale-[1.02]"
          >
            Agendar Consulta Privada
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function PartnerCard({
  img,
  name,
  role,
  bio,
}: {
  img: string;
  name: string;
  role: string;
  bio: string;
}) {
  return (
    <article className="group flex flex-col overflow-hidden border border-navy-900/10 bg-white transition-all hover:border-gold-500 hover:shadow-xl hover:shadow-navy-900/5">
      <div className="aspect-[4/5] overflow-hidden bg-navy-900/5">
        <img
          src={img}
          alt={name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
      </div>
      <div className="p-8 md:p-10">
        <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-gold-500">
          {role}
        </span>
        <h3 className="mt-4 font-serif text-2xl text-navy-900 md:text-3xl">{name}</h3>
        <div className="mt-5 h-px w-12 bg-gold-500" />
        <p className="mt-5 text-sm leading-relaxed text-navy-900/65">{bio}</p>
      </div>
    </article>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-serif text-3xl text-gold-400 tabular-nums">{value}</div>
      <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-stone-100/50">{label}</div>
    </div>
  );
}
